import { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Global, keyframes } from '@emotion/react';
import lscache from 'lscache';
import Swipe, { SwipeEvent } from 'react-easy-swipe';
import { Content } from '../../apiService';
import { SpacingIndex } from '../../theme';
import useBreakpoint from '../../useBreakpoint';
import EpisodeNav, { NAV_BUTTON_SIZE } from './EpisodeNav';
import InfoSection from './InfoSection';
import InfoSheet from './InfoSheet';
import RestrictionNotice, { Restriction } from './RestrictionNotice';
import ShortStage, { ShortStageProps } from './ShortStage';
import SwipeHint from './SwipeHint';

const HINT_KEY = 'shorts_swipe_hint_seen';

type Direction = 'next' | 'prev';

type ShortsContainerProps = {
  content: Content;
  activeFileId?: string;
  prevFileId?: string;
  nextFileId?: string;
  /** Every episode's file id in play order, used to tell which way a grid jump travels. */
  episodeIds: string[];
  /** Set when the episode can't be played — the notice takes the player's place. */
  restriction?: Restriction;
  onSelectEpisode: (fileId?: string) => void;
  stage: Omit<ShortStageProps, 'fileId' | 'onShowInfo' | 'onEnded' | 'hold'>;
};

export default function ShortsContainer({
  content,
  activeFileId,
  prevFileId,
  nextFileId,
  episodeIds,
  restriction,
  onSelectEpisode,
  stage,
}: ShortsContainerProps) {
  const isDesktop = useBreakpoint('md');
  const [infoOpen, setInfoOpen] = useState(false);
  const [direction, setDirection] = useState<Direction>('next');
  // `hintDue` is null until storage has been read on the client, so playback never starts
  // behind a hint that is about to appear. It stays true after dismissal, keeping the overlay
  // mounted long enough to fade; `hintVisible` is what drives its opacity.
  const [hintDue, setHintDue] = useState<boolean | null>(null);
  const [hintVisible, setHintVisible] = useState(false);

  useEffect(() => {
    const seen = !!lscache.get(HINT_KEY);
    setHintDue(!seen);
    setHintVisible(!seen);
  }, []);

  // An age or country restriction sits on the short, not on one of its episodes, so every
  // episode is behind the same notice: with nowhere to travel, the swipe, the arrows and the
  // grid stand down. A paywall is per episode — some of them may be free — so it leaves the
  // viewer their way around the short.
  const canNavigate = !restriction || restriction === 'subscription';
  // Coaching for a swipe that goes somewhere, which is exactly what a paywalled viewer needs
  // to find the free episodes. It is drawn after the notice, over it, and dismisses itself.
  const showHint = canNavigate && !isDesktop && hintDue === true;
  // Released the moment the fade starts, so the video is already coming up beneath it.
  const holdPlayback = !!restriction || hintDue === null || (!isDesktop && hintVisible);

  const dismissHint = useCallback(() => {
    lscache.set(HINT_KEY, true);
    setHintVisible(false);
  }, []);

  // Arrows and swipes already know their direction. A jump from the episode grid works it out
  // from where the target sits relative to what's playing, so picking an earlier episode
  // slides down rather than up.
  const selectEpisode = useCallback((fileId?: string, to?: Direction) => {
    setInfoOpen(false);
    dismissHint();
    setDirection(to ?? (
      episodeIds.indexOf(fileId || '') < episodeIds.indexOf(activeFileId || '') ? 'prev' : 'next'
    ));
    onSelectEpisode(fileId);
  }, [onSelectEpisode, dismissHint, episodeIds, activeFileId]);

  // Stable per episode, so the players re-bind their `ended` listener only when the target
  // actually moves. A no-op at either end of the list, which simply stops there.
  const goNext = useCallback(() => selectEpisode(nextFileId, 'next'), [selectEpisode, nextFileId]);
  const goPrev = useCallback(() => selectEpisode(prevFileId, 'prev'), [selectEpisode, prevFileId]);

  // A vertical drag that starts inside something scrollable belongs to that thing. Without
  // this, scrolling the player's quality or caption list would swipe to another episode.
  const dragOwnedByScroller = useRef(false);

  const handleSwipeStart = (event: SwipeEvent) => {
    dragOwnedByScroller.current = false;
    for (let node = event.target as HTMLElement | null; node && node !== document.body; node = node.parentElement) {
      const { overflowY } = window.getComputedStyle(node);
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
        dragOwnedByScroller.current = true;
        return;
      }
    }
  };

  const info = (
    <InfoSection
      content={content}
      activeFileId={activeFileId}
      episodesDisabled={!canNavigate}
      onSelectEpisode={selectEpisode}
    />
  );

  return (
    <>
      <Global styles={pageScrollLock} />

      <Layout>
        {isDesktop && <InfoPanel>{info}</InfoPanel>}

        <Stage>
          <SwipeArea
            tolerance={50}
            onSwipeStart={handleSwipeStart}
            onSwipeUp={() => canNavigate && !dragOwnedByScroller.current && goNext()}
            onSwipeDown={() => canNavigate && !dragOwnedByScroller.current && goPrev()}
          >
            <PlayerBox>
              <Viewport>
                {/* Keyed on the episode so the slide-in replays on every change. */}
                <Slide key={activeFileId} direction={direction}>
                  <ShortStage
                    {...stage}
                    fileId={activeFileId}
                    hold={holdPlayback}
                    onEnded={goNext}
                    onShowInfo={isDesktop ? undefined : () => setInfoOpen(true)}
                  />
                </Slide>
              </Viewport>

              {restriction && <RestrictionNotice reason={restriction} />}
              {showHint && <SwipeHint visible={hintVisible} onDismiss={dismissHint} />}

              {isDesktop && canNavigate && (
                <NavWrapper>
                  <EpisodeNav prevFileId={prevFileId} nextFileId={nextFileId} onSelect={selectEpisode} />
                </NavWrapper>
              )}
            </PlayerBox>
          </SwipeArea>
        </Stage>

        {!isDesktop && (
          <InfoSheet open={infoOpen} onClose={() => setInfoOpen(false)}>
            {info}
          </InfoSheet>
        )}
      </Layout>
    </>
  );
}

// The surface is exactly one viewport tall and nothing on it scrolls the page: what page
// scroll is left over only drags the player out from under the sheet, which is fixed while
// the player behind it is not. Taking it away also stands down the pull-to-refresh that a
// vertical swipe between episodes otherwise trips.
const pageScrollLock = {
  'html, body': {
    height: '100%',
    overflow: 'hidden',
    overscrollBehavior: 'none' as const,
  },
};

const slideIn = (from: string) => keyframes({
  from: { transform: `translateY(${from})`, opacity: 0.3 },
  to: { transform: 'translateY(0)', opacity: 1 },
});

const fromBelow = slideIn('100%');
const fromAbove = slideIn('-100%');

// The desktop row is the info panel, the stage's own padding, a rail on either side of the
// player for the episode arrows — which sit outside the player's edge — and the player itself.
// Everything but the player is a fixed width, so the player takes whatever is left over. That
// is what keeps the arrows inside their rail: with a fixed 600px player they had nowhere to go
// between md and lg and ended up drawn over the panel.
const PANEL_MAX_WIDTH = 35; // rem
const STAGE_PAD: SpacingIndex = 2; // Stage's horizontal padding, on both sides
const NAV_GAP: SpacingIndex = 2; // between the player's edge and the arrows

const Layout = styled.div(({ theme }) => {
  const rail = `calc(${theme.spacing(NAV_BUTTON_SIZE)} + ${theme.spacing(NAV_GAP)})`;
  // The panel, the stage's padding and a rail on either side of the player: the desktop row
  // minus the player itself.
  const reserved = `calc(var(--shorts-panel) + 2 * ${theme.spacing(STAGE_PAD)} + 2 * ${rail})`;

  return {
    display: 'flex',
    // dvh keeps the player clear of the mobile browser's collapsing toolbars.
    height: ['100vh', '100dvh'],
    overflow: 'hidden',
    [theme.breakpoints.up('md')]: {
      // The panel gives width back as the window narrows instead of holding 35rem all the way
      // down to md, where that would leave the player a sliver of what is left.
      '--shorts-panel': `min(${PANEL_MAX_WIDTH}rem, 40vw)`,
      '--shorts-player': `min(${theme.breakpoints.sm}px, calc(100vw - ${reserved}))`,
    },
  };
});

const InfoPanel = styled.aside(({ theme }) => ({
  flex: '0 0 auto',
  width: `var(--shorts-panel, ${PANEL_MAX_WIDTH}rem)`,
  margin: `${theme.spacing(6)} 0`,
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: `${theme.radius.full} 0 0 ${theme.radius.full}`,
  backgroundColor: theme.palette.background[600][0],
  // Only once the panel is at its full width is there room for the roomier padding.
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(6),
  },
}));

const Stage = styled.div(({ theme }) => ({
  position: 'relative',
  flex: 1,
  minWidth: 0,
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6, STAGE_PAD),
  },
}));

const SwipeArea = styled(Swipe)({
  display: 'flex',
  // Top-align so the 9:16 desktop player lines up with the info panel instead of floating
  // in the vertical center of the stage.
  alignItems: 'flex-start',
  justifyContent: 'center',
  height: '100%',
  // Vertical drags are ours; leave horizontal ones to the controls.
  touchAction: 'pan-x',
});

const PlayerBox = styled.div(({ theme }) => {
  // Stage's vertical padding on md+; the player height is the layout minus this on both sides.
  const stagePadY = theme.spacing(6);
  // Tallest 9:16 frame that fits the stage, before the --shorts-player cap.
  const tallestNineSixteen = `calc((100dvh - 2 * ${stagePadY}) * 9 / 16)`;
  const playerWidth = `min(var(--shorts-player, ${theme.breakpoints.sm}px), ${tallestNineSixteen})`;

  return {
    position: 'relative',
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.background[900][0],
    // No font-size override here on purpose: the player chrome sizes its icons in `em`, so any
    // font-size set on this box scales every control with it.
    //
    // `--short-player-vw` is 1% of the player's own width. The legacy chrome sizes itself in `vw`,
    // which makes the controls track the viewport rather than this panel — the `.short-player`
    // rules in Player/styles size off this instead, so they stay proportional to the box.
    // Below md the panel is already full width, so a real viewport unit is the correct value.
    '--short-player-vw': '1vw',
    [theme.breakpoints.up('md')]: {
      // Shorts are authored 9:16; match that so the usual short fills with no crop and no bars.
      aspectRatio: '9 / 16',
      width: playerWidth,
      height: 'auto',
      maxHeight: '100%',
      borderRadius: theme.radius.full,
      '--short-player-vw': `calc(${playerWidth} / 100)`,
    },
  };
});

// Clips the slide. Separate from PlayerBox so the desktop arrows, which sit outside the
// player's edge, aren't clipped away with it.
const Viewport = styled.div({
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  borderRadius: 'inherit',
});

const Slide = styled.div<{ direction: Direction }>(({ direction }) => ({
  position: 'absolute',
  inset: 0,
  animation: `${direction === 'next' ? fromBelow : fromAbove} 320ms ease-out`,
}));

const NavWrapper = styled.div(({ theme }) => ({
  position: 'absolute',
  zIndex: 2,
  bottom: theme.spacing(7),
  // The gap half of the rail Layout reserves; the buttons themselves fill the rest of it.
  left: `calc(100% + ${theme.spacing(NAV_GAP)})`,
}));
